import { useMemo, useState } from 'react';
import { ActionList, StatusMessage } from '../../components/ui';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import SortIcon from '../../components/ui/Sorting';
import SortSummary from '../../components/ui/SortSummary';
import { formatDateTime } from '../../helpers/formatDateTime';
import type { AttachmentGroupDto } from '../../services/attachments/attachment.types';

const COLUMNS = [
    { key: 'profileName', label: 'Profile Name' },
    { key: 'createdDateTime', label: 'Created' },
    { key: 'updatedDateTime', label: 'Updated' },
    { key: 'attachments', label: 'Attachments' },
] as const;

type SortColumn = typeof COLUMNS[number]['key'];

interface Props {
    groups: AttachmentGroupDto[];
    loading: boolean;
    error: string | null;
    onDismissError: () => void;
    editing: boolean;
    actionInFlight: number | null;
    onEdit: (group: AttachmentGroupDto) => void;
    onDelete: (group: AttachmentGroupDto) => void;
}

export function AttachmentGroupList({ groups, loading, error, onDismissError, editing, actionInFlight, onEdit, onDelete }: Props) {
    const [sortColumn, setSortColumn] = useState<SortColumn>('createdDateTime');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [hasAppliedSort, setHasAppliedSort] = useState(false);

    const handleSort = (column: SortColumn) => {
        setHasAppliedSort(true);
        setSortColumn(column);
        setSortOrder(previous => sortColumn === column ? (previous === 'asc' ? 'desc' : 'asc') : 'asc');
    };

    const resetSort = () => {
        setSortColumn('createdDateTime');
        setSortOrder('desc');
        setHasAppliedSort(false);
    };

    const sortedGroups = useMemo(() => [...groups].sort((a, b) => {
        let comparison: number;
        if (sortColumn === 'profileName') {
            const aName = (a.profileName || '').toLowerCase();
            const bName = (b.profileName || '').toLowerCase();
            comparison = aName < bName ? -1 : aName > bName ? 1 : 0;
        } else if (sortColumn === 'attachments') {
            comparison = (a.attachments?.length ?? 0) - (b.attachments?.length ?? 0);
        } else {
            const aDate = a[sortColumn] ? new Date(a[sortColumn]).getTime() : 0;
            const bDate = b[sortColumn] ? new Date(b[sortColumn]).getTime() : 0;
            comparison = aDate - bDate;
        }
        return comparison * (sortOrder === 'asc' ? 1 : -1);
    }), [groups, sortColumn, sortOrder]);

    return (
        <>
            <div className="alarm-content--heading" aria-hidden={editing}>
                <div>
                    <h3 className="heading heading__title--attachment-groups">Group List</h3>
                    {hasAppliedSort && (
                        <SortSummary
                            columnLabel={COLUMNS.find(column => column.key === sortColumn)!.label}
                            sortOrder={sortOrder}
                            onReset={resetSort}
                            resetAriaLabel="Reset sorting to newest first"
                        />
                    )}
                </div>
            </div>
            {loading && <LoadingSpinner message="Loading attachment groups…" />}
            {error && <StatusMessage kind="error" onClose={onDismissError}>{error}</StatusMessage>}
            {!loading && (
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                {COLUMNS.map(column => (
                                    <th key={column.key} onClick={() => handleSort(column.key)}
                                        aria-sort={sortColumn === column.key ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                                        className="th--sortable">
                                        {column.label} <SortIcon sortOrder={sortColumn === column.key ? sortOrder : null} />
                                    </th>
                                ))}
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedGroups.map(group => (
                                <tr key={group.id}>
                                    <td>{group.profileName}</td>
                                    <td>{formatDateTime(group.createdDateTime)}</td>
                                    <td>{formatDateTime(group.updatedDateTime ?? new Date().toISOString())}</td>
                                    <td>{group.attachments?.length ?? 0}</td>
                                    <td>
                                        <ActionList onDelete={() => onDelete(group)} onEdit={() => onEdit(group)} disabled={actionInFlight !== null} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
